/**
 * Jivara Design System
 * 
 * A comprehensive component library built for Justisio's modern interface.
 * Combines neo-brutalist aesthetics with clean, functional design.
 * 
 * Features:
 * - High contrast black/white palette with gray accents
 * - Neo-brutalist cards with offset shadows
 * - Fully-rounded buttons with hover animations
 * - Gradient text effects for emphasis
 * - Device mockups for showcasing interfaces
 * - Purposeful, subtle animations
 */

// Design tokens
export * from './tokens';

// Typography components
export { 
  DisplayHeading,
  SectionHeading,
  CardHeading,
  SubHeading,
  BodyText,
  Caption,
  GradientText,
  Highlight,
  Label,
  Typography 
} from './Typography';

// Button components
export { 
  Button,
  IconButton,
  PricedButton,
  ButtonGroup,
  LinkButton,
  MenuItem,
  MenuGroup,
  Buttons 
} from './Button';

// Card components
export { 
  BrutalCard,
  FeatureCard,
  InfoCard,
  StatCard,
  GlassCard,
  ListCard,
  Cards 
} from './Card';

// Badge and pill components
export { 
  Badge,
  StatusBadge,
  Pill,
  Tag,
  CountBadge,
  FeatureLabel,
  PriceTag,
  NotificationDot,
  BrutalLabel,
  Badges 
} from './Badge';

// Device mockup components
export { 
  PhoneMockup,
  TabletMockup,
  BrowserMockup,
  MonitorMockup,
  DeviceGroup,
  AppScreenSkeleton,
  Devices 
} from './DeviceMockup';

// Data display components (for field extraction, key-value pairs)
export {
  FieldRow,
  FieldList,
  FieldGrid,
  FieldCard,
  DataSection,
  StatDisplay,
  ExtractionResult,
  DataDisplays,
} from './DataDisplay';

// Layout components
export {
  Container,
  NavbarAlignedContainer,
  Section,
  HeroSection,
  DarkSection,
  Grid,
  SplitSection,
  CenteredContent,
  Spacer,
  Divider,
  FullWidthBackground,
  Layout
} from './Layout';

// Animation components
export { 
  Float,
  FadeIn,
  ScaleIn,
  HoverScale,
  StaggeredList,
  Pulse,
  Spin,
  Bounce,
  Shimmer,
  RevealOnScroll,
  Animations 
} from './Animation';

// Mascot/Character components
export {
  JivaraMascot,
  JivaraPositioned,
  JivaraPeeking,
  JivaraSpeaking,
  JivaraEmptyState,
  JivaraLoading,
  JivaraSuccess,
  JivaraHeroDecoration,
  JivaraFeature,
  JIVARA_POSES,
  Jivara,
  type JivaraPose,
  type JivaraSize,
} from './Mascot';

// Table components
export {
  BrutalTable,
  BrutalTableHeader,
  BrutalTableBody,
  BrutalTableFooter,
  BrutalTableRow,
  BrutalTableHead,
  BrutalTableCell,
  BrutalTableCaption,
  BrutalTableEmpty,
  Tables,
} from './Table';

// Sheet components
export {
  BrutalSheet,
  BrutalSheetTrigger,
  BrutalSheetClose,
  BrutalSheetContent,
  BrutalSheetHeader,
  BrutalSheetFooter,
  BrutalSheetTitle,
  BrutalSheetDescription,
  BrutalSheetSection,
  Sheets,
} from './Sheet';

// Form components
export {
  Input,
  TextArea,
  Checkbox,
  Radio,
  Select,
  Label as FormLabel,
  Forms,
} from './Form';

// Alert components
export {
  Alert,
  ToastCard,
  Alerts,
} from './Alert';

// Accordion components
export {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
  Accordions,
} from './Accordion';

// Tabs components
export {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  TabGroups,
} from './Tabs';

// Dialog components
export {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  DialogClose,
  DialogOverlay,
  DialogPortal,
  Dialogs
} from './Dialog';

// Breadcrumb components
export {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
  BreadcrumbEllipsis,
  Breadcrumbs
} from './Breadcrumb';

// Tooltip components
export {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  Tooltips
} from './Tooltip';

// Popover components
export {
  Popover,
  PopoverTrigger,
  PopoverContent,
  Popovers
} from './Popover';

// Avatar components
export {
  Avatar,
  AvatarImage,
  AvatarFallback,
  Avatars
} from './Avatar';

// Progress components
export {
  Progress,
  ProgressBars
} from './Progress';

// Switch components
export {
  Switch,
  Switches
} from './Switch';

// Slider components
export {
  Slider,
  Sliders
} from './Slider';

// Convenience groupings for namespaced imports
export const JivaraDesign = {
  Typography: () => import('./Typography').then(m => m.Typography),
  Buttons: () => import('./Button').then(m => m.Buttons),
  Cards: () => import('./Card').then(m => m.Cards),
  Badges: () => import('./Badge').then(m => m.Badges),
  Devices: () => import('./DeviceMockup').then(m => m.Devices),
  Layout: () => import('./Layout').then(m => m.Layout),
  Animations: () => import('./Animation').then(m => m.Animations),
  Tables: () => import('./Table').then(m => m.Tables),
  Sheets: () => import('./Sheet').then(m => m.Sheets),
  Forms: () => import('./Form').then(m => m.Forms),
  Alerts: () => import('./Alert').then(m => m.Alerts),
  Accordions: () => import('./Accordion').then(m => m.Accordions),
  Tabs: () => import('./Tabs').then(m => m.TabGroups),
  Dialogs: () => import('./Dialog').then(m => m.Dialogs),
  Breadcrumbs: () => import('./Breadcrumb').then(m => m.Breadcrumbs),
  Tooltips: () => import('./Tooltip').then(m => m.Tooltips),
  Popovers: () => import('./Popover').then(m => m.Popovers),
  Avatars: () => import('./Avatar').then(m => m.Avatars),
  ProgressBars: () => import('./Progress').then(m => m.ProgressBars),
  Switches: () => import('./Switch').then(m => m.Switches),
  Sliders: () => import('./Slider').then(m => m.Sliders),
};
